import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import './App.css';

// Critical components - loaded immediately
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import { PageLoader } from './components/common';
import MainLayout from './components/Layout/MainLayout';
import usePageTracking from './hooks/usePageTracking';

function AnalyticsTracker() {
  usePageTracking();
  return null;
}

// Deferred initializers — loaded after idle to avoid blocking first paint
function DeferredInitializers() {
  const ShoppableInitializer = lazy(() => import('./components/common/ShoppableInitializer'));
  return (
    <Suspense fallback={null}>
      <ShoppableInitializer />
    </Suspense>
  );
}

function IdleWrapper({ children }) {
  const [shouldRender, setShouldRender] = useState(false);
  useEffect(() => {
    if (typeof requestIdleCallback !== 'undefined') {
      const id = requestIdleCallback(() => setShouldRender(true), { timeout: 7000 });
      return () => cancelIdleCallback(id);
    } else {
      // Fallback for browsers without requestIdleCallback
      const id = setTimeout(() => setShouldRender(true), 200);
      return () => clearTimeout(id);
    }
  }, []);
  return shouldRender ? children : null;
}

// Redirect /share/bag/:bagSlug → /explore/bag/:bagSlug
function ShareRedirect() {
  const { bagSlug } = useParams();
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  const to = `/explore/bag/${bagSlug}${qs ? `?${qs}` : ''}`;
  return <Navigate to={to} replace />;
}

// Redirect /bags/:bagId → /explore/bag/:bagId
function BagToExploreRedirect() {
  const { bagId } = useParams();
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  const to = `/explore/bag/${bagId}${qs ? `?${qs}` : ''}`;
  return <Navigate to={to} replace />;
}

// Lazy-loaded auth components — only needed on user interaction, not first paint
const AuthModal = lazy(() => import('./components/Auth/AuthModal'));
const ForgetPassword = lazy(() => import('./components/Auth/ForgetPassword'));

// Lazy-loaded components - loaded only when route is accessed
// Landing & Marketing Pages
const LandingPage = lazy(() => import('./components/LandingPage/LandingPage'));
const HowItWorksPage = lazy(() => import('./components/HowItWorksPage/HowItWorksPage'));
const UseCasesPage = lazy(() => import('./components/UseCases/UseCasesPage'));

// Auth Pages
const LoginRedirect = lazy(() => import('./components/Auth/Login'));
const RegisterRedirect = lazy(() => import('./components/Auth/Register'));
const CheckEmail = lazy(() => import('./components/Auth/CheckEmail'));
const ResetPassword = lazy(() => import('./components/Auth/ResetPassword'));
const OAuthCallback = lazy(() => import('./components/Auth/OAuthCallback'));

// User Pages
const Profile = lazy(() => import('./components/Profile/Profile'));

// Core App Pages
const Bags = lazy(() => import('./components/Bags/Bags'));
const BagItems = lazy(() => import('./components/BagItems/BagItems'));
const Items = lazy(() => import('./components/Items/Items'));
const ExplorePage = lazy(() => import('./components/Explore/ExplorePage'));
const PublicBagDetailPage = lazy(() => import('./components/Explore/PublicBagDetailPage'));
const PropertyPage = lazy(() => import('./components/PropertyPage/PropertyPage'));

// Special Pages
const Earnings = lazy(() => import('./components/Earnings/Earnings'));
const AdminAffiliate = lazy(() => import('./components/AdminAffiliate/AdminAffiliate'));
const AdminFeatured = lazy(() => import('./components/AdminFeatured/AdminFeatured'));
const AdminProperties = lazy(() => import('./components/AdminProperties/AdminProperties'));

// Legal Pages
const PrivacyPolicyPage = lazy(() => import('./components/Legal/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('./components/Legal/TermsPage'));

// Contact Page
const ContactUs = lazy(() => import('./components/ContactUs/ContactUs'));

// About Page
const AboutPage = lazy(() => import('./components/About/AboutPage'));

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <IdleWrapper>
          <AnalyticsTracker />
          <DeferredInitializers />
        </IdleWrapper>
        <Suspense fallback={null}>
          <AuthModal />
        </Suspense>
        <div className="App">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Navigate to="/explore" replace />} />
              <Route path="/explore" element={<MainLayout><ExplorePage /></MainLayout>} />
              <Route path="/home" element={<LandingPage />} />
              <Route path="/login" element={<LoginRedirect />} />
              <Route path="/register" element={<RegisterRedirect />} />
              <Route path="/forgot-password" element={<MainLayout><ForgetPassword /></MainLayout>} />
              <Route path="/check-email" element={<MainLayout><CheckEmail /></MainLayout>} />
              <Route path="/reset-password" element={<MainLayout><ResetPassword /></MainLayout>} />
              <Route path="/auth/callback" element={<OAuthCallback />} />
              <Route path="/profile" element={<MainLayout><Profile /></MainLayout>} />
              <Route path="/account-settings" element={<Navigate to="/profile" replace />} />
              <Route path="/bags" element={<MainLayout><Bags /></MainLayout>} />
              <Route path="/bags/:bagId/items" element={<MainLayout><BagItems /></MainLayout>} />
              <Route path="/bags/:bagId" element={<BagToExploreRedirect />} />
              <Route path="/items" element={<MainLayout><Items /></MainLayout>} />
              <Route path="/earnings" element={<MainLayout><Earnings /></MainLayout>} />
              <Route path="/admin/affiliate" element={<MainLayout><AdminAffiliate /></MainLayout>} />
              <Route path="/admin/featured" element={<MainLayout><AdminFeatured /></MainLayout>} />
              <Route path="/admin/properties" element={<MainLayout><AdminProperties /></MainLayout>} />
              <Route path="/privacy" element={<MainLayout><PrivacyPolicyPage /></MainLayout>} />
              <Route path="/terms" element={<MainLayout><TermsPage /></MainLayout>} />
              <Route path="/contact" element={<MainLayout><ContactUs /></MainLayout>} />
              <Route path="/about" element={<MainLayout><AboutPage /></MainLayout>} />
              <Route path="/how-it-works" element={<MainLayout><HowItWorksPage /></MainLayout>} />
              <Route path="/use-cases" element={<MainLayout><UseCasesPage /></MainLayout>} />
              <Route path="/explore/bag/:bagSlug" element={<MainLayout><PublicBagDetailPage /></MainLayout>} />
              <Route path="/share/bag/:bagSlug" element={<ShareRedirect />} />
              <Route path="/properties/:propertySlug" element={<PropertyPage />} />
            </Routes>
          </Suspense>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;